import type {
  Ranges,
  ResultHistory,
  ResultHistoryWithRange,
} from '../../../../../services/MedicalResultsService';

export type ResultViewMode = 'personalized' | 'standard';

export type AnnotatedHistoryDisplayModel = {
  effectiveStatus: 'in-range' | 'attention' | 'optimal' | 'default' | 'pending';
  interpretationText: string;
  providerQuote: string | null;
  rangeSource: {
    type: 'withRange' | 'withoutRange' | 'pending';
    ranges?: Ranges;
  };
};

/**
 * TypeScript collapses the annotation type across the history response union
 * members, dropping fields only present on the `withRange` variant. We
 * intersect the resolved base with the missing withRange-specific fields.
 */
type WithRangeAnnotation = NonNullable<ResultHistoryWithRange['annotation']> & {
  ranges?: Ranges;
  result?: {
    status: 'in-range' | 'attention' | 'optimal';
    text: string;
    interpretationText: string;
  };
  statusMatchesStandardRange?: boolean;
};

const hasNonEmptyRanges = (ranges: unknown): ranges is Ranges =>
  !!ranges &&
  typeof ranges === 'object' &&
  Object.keys(ranges as object).length > 0;

export const hasMeaningfulPersonalizedDetail = (
  item: ResultHistory,
): boolean => {
  const ann = item.annotation as WithRangeAnnotation | undefined;
  if (!ann) return false;
  if (ann.notes?.some(n => n.trim())) {
    return true;
  }
  if (item.type !== 'withRange') return false;
  if (hasNonEmptyRanges(ann.ranges)) return true;
  const pr = ann.personalizedRange;
  return typeof pr?.high === 'number' || typeof pr?.low === 'number';
};

const mergePersonalizedRangeIntoItemRanges = (
  item: ResultHistoryWithRange,
  pr: NonNullable<WithRangeAnnotation['personalizedRange']>,
): Ranges => {
  const baseBand = item.ranges?.['in-range'] ?? {
    label: '',
    status: 'in-range' as const,
  };
  return {
    ...item.ranges,
    'in-range': {
      ...baseBand,
      ...(typeof pr.low === 'number' ? { lower: pr.low } : {}),
      ...(typeof pr.high === 'number' ? { upper: pr.high } : {}),
      label: baseBand.label,
      status: baseBand.status ?? 'in-range',
    },
  } as Ranges;
};

export const getRangeSourceForLens = (
  item: ResultHistory,
  mode: ResultViewMode,
): AnnotatedHistoryDisplayModel['rangeSource'] => {
  if (item.type === 'pending') return { type: 'pending' };
  if (item.type !== 'withRange') return { type: item.type };
  if (mode === 'standard') return { type: 'withRange', ranges: item.ranges };

  const a = item.annotation as WithRangeAnnotation | undefined;
  if (!a) return { type: 'withRange', ranges: item.ranges };

  if (hasNonEmptyRanges(a.ranges)) {
    return { type: 'withRange', ranges: a.ranges };
  }
  const pr = a.personalizedRange;
  if (typeof pr?.high === 'number' || typeof pr?.low === 'number') {
    return {
      type: 'withRange',
      ranges: mergePersonalizedRangeIntoItemRanges(item, pr),
    };
  }
  return { type: 'withRange', ranges: item.ranges };
};

export const getEffectiveStatusForLens = (
  item: ResultHistory,
  mode: ResultViewMode,
): AnnotatedHistoryDisplayModel['effectiveStatus'] => {
  if (item.type === 'pending') return 'pending';
  if (mode === 'standard') {
    return item.result.status;
  }
  const fromAnnotation =
    item.type === 'withRange'
      ? (item.annotation as WithRangeAnnotation | undefined)?.result?.status
      : undefined;
  if (fromAnnotation) {
    return fromAnnotation;
  }
  return item.result.status;
};

const interpretationForItem = (
  item: ResultHistory,
  mode: ResultViewMode,
): string => {
  if (item.type === 'pending') return '';
  if (mode === 'standard') return item.result.interpretationText;

  const ext =
    item.type === 'withRange'
      ? (item.annotation as WithRangeAnnotation | undefined)
      : undefined;
  const interp = ext?.result?.interpretationText || ext?.result?.text;
  return interp?.trim() || item.result.interpretationText;
};

export const getProviderQuote = (
  item: ResultHistory,
  mode: ResultViewMode,
): string | null => {
  if (mode !== 'personalized') return null;
  const first = item.annotation?.notes?.find(n => n.trim());
  return first?.trim() ?? null;
};

export const getAnnotatedHistoryDisplayModel = (
  item: ResultHistory,
  mode: ResultViewMode,
): AnnotatedHistoryDisplayModel => ({
  effectiveStatus: getEffectiveStatusForLens(item, mode),
  interpretationText: interpretationForItem(item, mode),
  providerQuote: getProviderQuote(item, mode),
  rangeSource: getRangeSourceForLens(item, mode),
});
